<script setup lang="ts">
import type { Product } from '~/types'

const props = defineProps<{
  product: Product
}>()

const { addItem, isInCart, getQuantity, updateQuantity } = useQuoteCart()

const quantity = ref(1)

const inCart = computed(() => isInCart(props.product.id))
const cartQuantity = computed(() => getQuantity(props.product.id))

const handleAddToCart = () => {
  if (quantity.value <= 0) {
    quantity.value = 1
    return
  }

  addItem({
    productId: props.product.id,
    productSlug: props.product.slug,
    productName: props.product.name,
    productSku: props.product.stock_code,
    packSize: props.product.pack_size,
    unit: props.product.unit,
    imageUrl: props.product.image_url
  }, quantity.value)
  quantity.value = 1
}

const handleUpdateQuantity = (newQty: number) => {
  if (newQty < 0) return
  updateQuantity(props.product.id, newQty)
}

const packSizeDisplay = computed(() => {
  if (!props.product.pack_size) return null
  return `${props.product.pack_size} ${props.product.unit}`
})

const productLink = computed(() => `/catalogo/${props.product.slug}`)

// Check if image is external (from axalta.com)
const isExternalImage = computed(() =>
  props.product.image_url?.startsWith('http')
)
</script>

<template>
  <article
    class="group flex bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-200 border border-neutral-200/60"
  >
    <!-- Image Area - Compact square on left -->
    <NuxtLink
      :to="productLink"
      :aria-label="`Ver detalles de ${product.name}`"
      class="relative shrink-0 w-28 sm:w-36 bg-gradient-to-br from-neutral-50 to-neutral-100 overflow-hidden"
    >
      <!-- Use regular img for external URLs to avoid Netlify image optimization issues -->
      <img
        v-if="product.image_url && isExternalImage"
        :src="product.image_url"
        :alt="`${product.name} - ${product.stock_code}`"
        class="w-full h-full object-contain p-3 transition-transform duration-300 group-hover:scale-105"
        width="150"
        height="150"
        loading="lazy"
        decoding="async"
      />
      <!-- Use NuxtImg for local/internal images -->
      <NuxtImg
        v-else-if="product.image_url"
        :src="product.image_url"
        :alt="`${product.name} - ${product.stock_code}`"
        class="w-full h-full object-contain p-3 transition-transform duration-300 group-hover:scale-105"
        width="150"
        height="150"
        loading="lazy"
        decoding="async"
        sizes="150px"
        format="webp"
      />
      <div v-else class="w-full h-full flex items-center justify-center">
        <UIcon name="i-heroicons-photo" class="w-10 h-10 text-neutral-300" />
      </div>
    </NuxtLink>

    <!-- Content Area - Fills remaining space -->
    <div class="flex-1 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 p-4">
      <!-- Product Info -->
      <div class="flex-1 min-w-0">
        <!-- Product Name - Full display, no truncation -->
        <NuxtLink
          :to="productLink"
          class="block font-semibold text-sm sm:text-base text-neutral-900 hover:text-primary-600 transition-colors leading-snug mb-2"
        >
          {{ product.name }}
        </NuxtLink>

        <!-- Specs row - SKU, Pack Size, Product Line all inline -->
        <div class="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-neutral-500">
          <span class="px-2 py-0.5 font-semibold text-neutral-700 bg-neutral-100 rounded">
            {{ product.stock_code }}
          </span>
          <span v-if="packSizeDisplay">
            {{ packSizeDisplay }}
          </span>
          <template v-if="product.product_line?.name">
            <span class="text-neutral-300">·</span>
            <span class="text-primary-600 font-medium">{{ product.product_line.name }}</span>
            <span v-if="product.component_type" class="text-neutral-400">{{ product.component_type }}</span>
          </template>
        </div>
      </div>

      <!-- Action Area - Wrapped in ClientOnly to prevent hydration mismatch -->
      <ClientOnly>
        <div class="shrink-0 flex flex-col sm:flex-row items-end sm:items-center gap-2 sm:gap-3 pt-2 sm:pt-0 border-t sm:border-t-0 sm:border-l border-neutral-100 sm:pl-4">
          <!-- Not in cart -->
          <template v-if="!inCart">
            <div class="flex items-center gap-2">
              <label :for="`qty-${product.id}`" class="sr-only">Cantidad para {{ product.name }}</label>
              <UInput
                :id="`qty-${product.id}`"
                v-model.number="quantity"
                type="number"
                min="1"
                step="1"
                class="w-16"
                size="sm"
                :aria-label="`Cantidad para ${product.name}`"
                :ui="{
                  base: 'text-center font-semibold'
                }"
              />
              <UButton
                color="primary"
                size="sm"
                class="font-medium"
                @click="handleAddToCart"
              >
                Agregar
              </UButton>
            </div>
          </template>

          <!-- In cart -->
          <template v-else>
            <div class="flex items-center gap-2">
              <UButton
                color="primary"
                variant="soft"
                size="xs"
                icon="i-heroicons-minus"
                square
                :aria-label="`Reducir cantidad de ${product.name}`"
                @click="handleUpdateQuantity(cartQuantity - 1)"
              />
              <span class="w-8 text-center font-bold text-base tabular-nums" aria-live="polite">
                {{ cartQuantity }}
              </span>
              <UButton
                color="primary"
                variant="soft"
                size="xs"
                icon="i-heroicons-plus"
                square
                :aria-label="`Aumentar cantidad de ${product.name}`"
                @click="handleUpdateQuantity(cartQuantity + 1)"
              />
            </div>
            <div class="flex items-center gap-1.5 px-2 py-1 bg-emerald-50 rounded-full text-emerald-600">
              <UIcon name="i-heroicons-check-circle-solid" class="w-4 h-4 shrink-0" />
              <span class="text-xs font-medium whitespace-nowrap">En cotización</span>
            </div>
          </template>
        </div>
        <template #fallback>
          <div class="shrink-0 flex items-center gap-2 pt-2 sm:pt-0 border-t sm:border-t-0 sm:border-l border-neutral-100 sm:pl-4">
            <UButton
              color="primary"
              size="sm"
              class="font-medium"
              disabled
            >
              Agregar
            </UButton>
          </div>
        </template>
      </ClientOnly>
    </div>
  </article>
</template>
